import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext, useEffect } from 'react';

import { InitialLoading } from '@/components/ui/InitialLoading';
import { useInitialLoading } from '@/hooks/useInitialLoading';
import { markPerformance } from '@/utils/performanceMonitor';

interface InitialLoadingContextType {
  /**
   * Whether the application is in its initial loading state
   */
  isInitialLoading: boolean;
}

const InitialLoadingContext = createContext<InitialLoadingContextType | null>(
  null,
);

interface InitialLoadingProviderProps {
  children: ReactNode;
  /**
   * Minimum loading time in milliseconds
   */
  minLoadingTime?: number;
  /**
   * Enable debug logging
   */
  debug?: boolean;
}

/**
 * Provider component that manages the initial loading state
 * Shows a loading screen while fonts and resources are loading
 */
export function InitialLoadingProvider({
  children,
  minLoadingTime = 800,
  debug = import.meta.env.DEV,
}: InitialLoadingProviderProps): ReactElement {
  const isInitialLoading = useInitialLoading({
    minLoadingTime,
    debug,
  });

  // Mark performance metrics when loading completes
  useEffect(() => {
    if (!isInitialLoading) {
      markPerformance('initial-resources-loaded');

      if (debug) {
        console.debug('[InitialLoadingProvider] Initial loading complete');
      }
    }
  }, [isInitialLoading, debug]);

  return (
    <InitialLoadingContext.Provider value={{ isInitialLoading }}>
      {isInitialLoading && <InitialLoading />}
      {children}
    </InitialLoadingContext.Provider>
  );
}

/**
 * Hook to access the initial loading state
 */
export function useInitialLoadingContext(): InitialLoadingContextType {
  const context = useContext(InitialLoadingContext);

  if (context === null) {
    throw new Error(
      'useInitialLoadingContext must be used within an InitialLoadingProvider',
    );
  }

  return context;
}
